/**
 * reviewCooldown — web port of the iOS review-session cooldown
 * (client-native/src/stores/globalStore.ts:
 * `startReviewCooldown` / `clearReviewCooldown` / `isReviewCooldownActive`,
 * persisted via `globalStore.reviewCooldownEndTime`).
 *
 * After a user exits a review session, iOS suppresses the review badge for a
 * short window ("done for now") so try-again cards don't immediately re-surface
 * an "X cards left" count. The end-time is persisted (MMKV on iOS) so it
 * survives reloads.
 *
 * We mirror that with a localStorage-backed end timestamp, matching the
 * existing web pattern in `dailyReviewBadge.ts` (no @legendapp store on web —
 * the review badge state is plain localStorage). A tiny subscription layer
 * gives React reactivity equivalent to iOS's `useValue(reviewCooldownEndTime)`.
 */

import { useEffect, useState } from "react";

const END_TIME_KEY = "reviewCooldownEndTime";

/**
 * How many seconds after exiting a review session to show the "done" state.
 * iOS: REVIEW_COOLDOWN_SECONDS_PROD = 120 (2 minutes) as of #763.
 */
export const REVIEW_COOLDOWN_SECONDS = 120;

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

function readEndTime(): number | null {
  try {
    const raw = localStorage.getItem(END_TIME_KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Start the review cooldown timer. Called when the user exits a review session.
 * iOS ref: `startReviewCooldown`.
 */
export function startReviewCooldown(): void {
  try {
    const endTime = Date.now() + REVIEW_COOLDOWN_SECONDS * 1000;
    localStorage.setItem(END_TIME_KEY, String(endTime));
  } catch {
    /* storage unavailable — non-fatal */
  }
  emit();
}

/**
 * Whether the cooldown is currently active. Pure, side-effect free — safe to
 * call during render. iOS ref: `isReviewCooldownActive`.
 */
export function isReviewCooldownActive(): boolean {
  const endTime = readEndTime();
  if (endTime === null) return false;
  return Date.now() < endTime;
}

/**
 * Reactive equivalent of iOS `useValue(globalStore.reviewCooldownEndTime)`.
 * Re-renders when the cooldown is started/cleared and, while active, schedules
 * a single re-render at the exact moment the cooldown expires so the badge
 * un-suppresses without a manual refresh.
 */
export function useReviewCooldownActive(): boolean {
  const [active, setActive] = useState(isReviewCooldownActive);

  useEffect(() => {
    const sync = () => setActive(isReviewCooldownActive());

    listeners.add(sync);
    // Cross-tab: another tab starting/clearing the cooldown writes localStorage.
    window.addEventListener("storage", sync);

    // While active, fire a re-render exactly when the cooldown lapses.
    let timer: number | undefined;
    const endTime = readEndTime();
    if (endTime !== null) {
      const ms = endTime - Date.now();
      if (ms > 0) timer = window.setTimeout(sync, ms);
    }

    return () => {
      listeners.delete(sync);
      window.removeEventListener("storage", sync);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [active]);

  return active;
}
